import styles from './login.scss';
import React, { useState, useCallback, useEffect } from 'react';
import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import classnames from 'classnames/bind';
import LogoPNG from '@/images/logo.png';
import * as yup from 'yup';
import { Form, Row, Button } from 'react-bootstrap';
import { useLoginMutation } from '@/services/auth.api';
import LanguageSelect from '@/components/language-select/language-select';
import LoginBasicBox from '@/components/auth/login-basic-box/login-basic-box';
import { LoginResponse } from '@/services/api.types';
import { useActivateTotpMutation } from '@/services/sso.api';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import PasswordInput from '@/components/PasswordInput';
import { REDIRECT_SEARCH_PARAM_KEY } from '@/constants/system';
import { useDispatch } from 'react-redux';
import { useLevvelTranslation } from '@/hooks/useLevvelTranslation';
import { useRuntimeConfig } from '@/hooks/useRuntimeConfig';
import { Divider } from 'antd';
import { advanceLoginStep } from '@/pages/Auth/login-step';
import { resolveUfsHost } from '@/utils/ufs.utils';
import {
  ERROR_REF_PARAM,
  SLO_ERROR_PARAM,
  SSO_ERROR_PARAM,
  correlationRef,
  sloErrorCodeForLog,
  ssoErrorMessageKey,
} from './sso-error';
const cx = classnames.bind(styles);

type LoginForm = {
  account: string;
  pwd: string;
};

const defaultFormValue: LoginForm = {
  account: '',
  pwd: '',
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const account = (state as any)?.account;

  // Employee login related state
  const [isEmployeeLoading, setIsEmployeeLoading] = useState(false);
  // SSO exclusivity (contract §0.5): set when password login hits HTTP 423 for an
  // account that must log in via SSO — drives the inline "use SSO" message.
  const [ssoExclusive, setSsoExclusive] = useState(false);
  const [activateTotp, { isLoading: isActivateTotpLoading }] =
    useActivateTotpMutation();

  const { lt } = useLevvelTranslation();
  const config = useRuntimeConfig();
  const [login, { isLoading, isError }] = useLoginMutation();

  const schema = yup
    .object({
      account: yup.string().trim().required('User name cannot be empty'),
      pwd: yup.string().trim().required('Must input a valid password'),
    })
    .required();

  const {
    register,
    control,
    watch,
    handleSubmit,
    getValues,
    formState: { errors, isValid },
  } = useForm<LoginForm>({
    resolver: yupResolver(schema),
    defaultValues: { ...defaultFormValue, ...{ account } },
  });

  watch('account');

  // SSO callback failure (see ./sso-error.ts). ufs sends the browser back to this
  // same page with an opaque `sso_error` code in place of a `totp` when the
  // IdP-callback step fails, and the two are mutually exclusive — so the effect
  // below still sees no totp and the successful SSO path is unaffected.
  //
  // The PRESENCE of the param, not its value, is what switches the page into the
  // error state: a code this build does not recognise must still explain itself
  // rather than fall through to an idle login form that says nothing happened.
  const ssoErrorCode = searchParams.get(SSO_ERROR_PARAM);
  const ssoErrorRefId = correlationRef(searchParams.get(ERROR_REF_PARAM));

  // SLO failure (see ./sso-error.ts). The OTHER redirect that lands here: ufs now
  // says on the way back from a sign-out whether the IdP actually ended the
  // session, and until this effect existed nothing read that — the one signal the
  // server has about a logout that did not happen died in the address bar.
  //
  // ⚠️ This is NOT the `ssoErrorCode` gate below, and must never become it: a
  // failed LOGOUT is not a failed LOGIN, and rendering the login-failure page for
  // one would be a worse lie than saying nothing. Nothing here is user-facing —
  // sso-error.ts carries why (SLO fails for everyone today, so a visible notice is
  // a product decision about every SSO logout, not a fix).
  const sloErrorCode = searchParams.get(SLO_ERROR_PARAM);
  const sloErrorRefId = correlationRef(searchParams.get(ERROR_REF_PARAM));

  useEffect(() => {
    if (sloErrorCode === null) {
      return;
    }
    console.error(
      `[slo] single logout rejected: ${sloErrorCodeForLog(sloErrorCode)}` +
        (sloErrorRefId ? ` (ref ${sloErrorRefId})` : '')
    );
    // Then drop the spent params, for handleBackToLogin's reason plus one of its
    // own: `replace` keeps Back off a URL whose entire content is a report of
    // something that already finished, and leaving the params on would log the
    // same failure again on every refresh of this page.
    navigate('/login', { replace: true });
  }, [sloErrorCode, sloErrorRefId]);

  useEffect(() => {
    if (searchParams.get('totp')) {
      getTotpToken();
    }
  }, [searchParams]);

  const getTotpToken = () => {
    const totp = location.search.split('totp=')[1];
    activateTotp(totp || '')
      .unwrap()
      .then((resp: LoginResponse) => {
        // Straight through the shared step-router (batch 004, ADR-9). This used to
        // hand-wrap a bare `{token}` into `{completed:{token}}`, which only worked
        // while SSO could not be held at a gate. It can now: SSO logins pass the
        // ToS/PP gate, so the reply is a full oneof and a pending branch must reach
        // /tos-and-pp rather than be flattened away. `flow: 'sso'` is what tells the
        // consent page to accept against POST /v1/sso/legal/accept.
        advanceLoginStep({
          resp,
          navigate,
          dispatch,
          flow: 'sso',
        });
      })
      .catch(error => {
        console.error('Error activating totp:', error);
        setIsEmployeeLoading(false);
      });
  };

  const submitLoginForm = (formData: LoginForm) => {
    setSsoExclusive(false);
    try {
      login({
        username: formData.account?.trim(),
        password: formData.pwd,
      })
        .unwrap()
        .then((resp: LoginResponse) => {
          // Backend-enforced multistep (ADR-03): drive off the step response body.
          // completed → land; pending → route to the gate named by `next`
          // (MFA → /login-sms, PWD → /reset-pass). See login-step.ts.
          advanceLoginStep({
            resp,
            navigate,
            dispatch,
            redirect: searchParams.get(REDIRECT_SEARCH_PARAM_KEY),
            account: formData.account?.trim(),
          });
        })
        .catch(rejected => {
          // SSO exclusivity (contract §0.5): the account has a live SAML
          // credential → password login is refused with HTTP 423 Locked (403 is
          // reserved for RBAC denials). Show the inline "use SSO" message; do NOT
          // auto-redirect (that was legacy v0). Generic bad creds stay 401 →
          // isError → the standard credential message.
          if (rejected?.status === 423) {
            setSsoExclusive(true);
            return;
          }
          console.error(rejected);
        });
    } catch (e) {
      console.log(e);
    }
  };

  // Create form to trigger SSO redirect (workaround for CORS limitations)
  const createSSOForm = useCallback((ssoUrl: string) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = ssoUrl;
    // form.target = '_blank';
    form.style.display = 'none';

    return form;
  }, []);

  // Handle CORS limitation workaround
  const handleCORSWorkaround = useCallback(
    (ssoUrl: string) => {
      const form = createSSOForm(ssoUrl);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      console.log('Form submitted - SSO should open in new tab');
    },
    [createSSOForm]
  );

  // Handle SSO redirect - use form submission directly, let browser handle redirect
  const handleSSORedirect = useCallback(() => {
    // const ssoUrl =
    //   process.env.NODE_ENV === 'development'
    //     ? '/api/ufs/v1/oiosaml3/sso'
    //     : `${environment.ufsApiHost}/v1/oiosaml3/sso`;
    const ssoUrl = `${resolveUfsHost()}/api/v1/oiosaml3/sso`;

    console.log('Opening SSO in new tab using form submission');
    handleCORSWorkaround(ssoUrl);
  }, [handleCORSWorkaround]);

  // Employee login handler function
  const handleEmployeeLogin = useCallback(async () => {
    setIsEmployeeLoading(true);
    handleSSORedirect();
  }, [navigate, dispatch, handleSSORedirect]);

  // Drop the params and stay on this route: the idle login view comes back, so the
  // user can start Employee Login again (or fall back to a password login) instead
  // of being stranded. `replace` so Back does not walk into the spent error URL.
  const handleBackToLogin = () => {
    navigate('/login', { replace: true });
  };

  if (ssoErrorCode !== null) {
    return (
      <LoginBasicBox>
        <div className={styles.login}>
          <header className={cx([styles.header])}>
            <div className={cx(['text-center', 'logoTitle'])}>
              <div>
                <img
                  className={styles.logo}
                  src={config?.style?.logo || LogoPNG}
                />
              </div>
            </div>
          </header>
          <main className={cx([styles.main])}>
            <section className={cx([styles.languageSelect])}>
              <LanguageSelect />
            </section>
            <section className={cx([styles.ssoError])}>
              <h3 className={cx(['title-bold', styles.ssoErrorTitle])}>
                {lt('auth_sso_error_title')}
              </h3>
              <p className={cx([styles.ssoErrorDesc])} role="alert">
                {lt(ssoErrorMessageKey(ssoErrorCode))}
              </p>
              {ssoErrorRefId && (
                <p className={cx([styles.ssoErrorRef])}>
                  {lt('auth_sso_error_reference', { ref: ssoErrorRefId })}
                </p>
              )}
            </section>
            <section>
              <Button
                className={cx([
                  'title-semiBold',
                  'd-flex align-items-center justify-content-center',
                  styles.loginBtn,
                ])}
                onClick={handleBackToLogin}
                type="button">
                {lt('auth_sso_error_back_to_login')}
              </Button>
            </section>
          </main>
        </div>
      </LoginBasicBox>
    );
  }

  let forgotPwdUrl = '/forgot-password';
  if (getValues().account) {
    forgotPwdUrl = `${forgotPwdUrl}?username=${getValues().account}`;
  }

  return (
    <LoginBasicBox>
      <div className={styles.login}>
        <header className={cx([styles.header])}>
          <div className={cx(['text-center', 'logoTitle'])}>
            <div>
              <img
                className={styles.logo}
                src={config?.style?.logo || LogoPNG}
              />
            </div>
          </div>
        </header>
        <main className={cx([styles.main])}>
          <section>
            <section className={cx([styles.languageSelect])}>
              <LanguageSelect />
            </section>
            <Form noValidate>
              <Row className={cx([styles.formRow])}>
                <Form.Group controlId="account" className={cx(['account'])}>
                  <Form.Control
                    {...register('account')}
                    className={cx([styles.formRowInput])}
                    type="text"
                    autoComplete="off"
                    isInvalid={isError}
                    placeholder={lt('cdb_login_user_name_text')}
                  />
                </Form.Group>
              </Row>
              <Row className={cx([styles.formRow])}>
                <Form.Group controlId="pwd" className={cx(['pwd'])}>
                  <Controller
                    name="pwd"
                    control={control}
                    render={({ field }) => (
                      <PasswordInput
                        {...field}
                        className={cx([styles.formRowInput, styles.pwdInput])}
                        placeholder={lt('cdb_login_password_text')}
                        isInvalid={!!errors.pwd || isError}
                      />
                    )}
                  />
                </Form.Group>
              </Row>
              {(isError || ssoExclusive) && (
                <div
                  className={cx([
                    'd-flex align-items-center',
                    styles.errorMessage,
                  ])}>
                  <span className={cx(['iconfont icon-ico-warning'])} />
                  &nbsp;
                  <span>
                    {lt(
                      ssoExclusive
                        ? 'auth_sso_exclusive_use_sso_text'
                        : 'cdb_incorrect_credential_text'
                    )}
                  </span>
                </div>
              )}
              <div className={cx(['mb-2 d-flex justify-content-end'])}>
                <Link to={forgotPwdUrl} className={styles.link}>
                  {lt('cdb_login_forget_password_text')}
                </Link>
              </div>
            </Form>
          </section>
          <section>
            <Button
              className={cx([
                'title-semiBold',
                'd-flex align-items-center justify-content-center',
                styles.loginBtn,
                config?.feature?.faelles?.a?.enabled ? 'mt-4' : '',
              ])}
              disabled={isLoading || !isValid || isActivateTotpLoading}
              onClick={handleSubmit(submitLoginForm)}
              variant={
                config?.feature?.faelles?.a?.enabled
                  ? 'outline-primary'
                  : 'primary'
              }
              type="button">
              {isLoading && (
                <span>
                  <span
                    className="spinner-border spinner-border-md"
                    role="status"
                    aria-hidden="true"
                  />
                  &nbsp;&nbsp;
                </span>
              )}
              {lt('cdb_login_login_text')}
            </Button>
          </section>
          {config?.feature?.faelles?.a?.enabled && (
            <section>
              <Divider
                plain
                style={{ color: '#656D73', borderColor: '#B7B7B7' }}>
                {lt('cdb_or_text')}
              </Divider>
              <Button
                className={cx([
                  'title-semiBold',
                  'd-flex align-items-center justify-content-center',
                  styles.loginBtn,
                ])}
                disabled={isEmployeeLoading || isActivateTotpLoading}
                onClick={handleEmployeeLogin}
                type="button">
                {isEmployeeLoading && (
                  <span>
                    <span
                      className="spinner-border spinner-border-md"
                      role="status"
                      aria-hidden="true"
                    />
                    &nbsp;&nbsp;
                  </span>
                )}
                {lt('cdb_employee_login_login_text')}
              </Button>
            </section>
          )}
        </main>
      </div>
    </LoginBasicBox>
  );
};

export default Login;
